import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getAuth, createUserWithEmailAndPassword } from 'firebase/auth';
import { getFirestore, doc, setDoc, collection, addDoc } from 'firebase/firestore';
import { FaBuilding, FaEnvelope, FaLock, FaUser, FaPhone, FaMapMarkerAlt, FaUpload, FaEye, FaEyeSlash, FaUserTie, FaArrowLeft, FaArrowRight, FaCheck } from 'react-icons/fa';
import { toast } from 'react-hot-toast';

export const BusinessRegister: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    // Step 1
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    // Step 2
    country: '',
    businessIdType: '',
    businessId: '',
    companyName: '',
    companyType: '',
    // Step 3
    address: '',
    phone: '',
    businessEmail: '',
    employeeCount: '',
    // Step 4
    logo: null as File | null
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        logo: file
      }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const validateStep = (step: number) => {
    switch (step) {
      case 1:
        if (!formData.firstName || !formData.lastName || !formData.email || !formData.password || !formData.confirmPassword) {
          setError(t('auth.allFieldsRequired'));
          return false;
        }
        if (formData.password !== formData.confirmPassword) {
          setError(t('auth.passwordsDoNotMatch'));
          return false;
        }
        break;
      case 2:
        if (!formData.country || !formData.businessIdType || !formData.businessId || !formData.companyName || !formData.companyType) {
          setError(t('auth.allFieldsRequired'));
          return false;
        }
        break;
      case 3:
        if (!formData.address || !formData.phone || !formData.businessEmail || !formData.employeeCount) {
          setError(t('auth.allFieldsRequired'));
          return false;
        }
        break;
      case 4:
        if (!formData.logo) {
          setError(t('auth.logoRequired'));
          return false;
        }
        break;
    }
    return true;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setError('');
      setCurrentStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => prev - 1);
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
      setLoading(false);
      return;
    }

    if (!validateStep(currentStep)) {
      setLoading(false);
      return;
    }

    try {
      const auth = getAuth();
      const db = getFirestore();

      // Crear usuario en Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );

      // Crear documento de empresa
      const businessRef = await addDoc(collection(db, 'businesses'), {
        country: formData.country,
        businessIdType: formData.businessIdType,
        businessId: formData.businessId,
        companyName: formData.companyName,
        companyType: formData.companyType,
        address: formData.address,
        phone: formData.phone,
        businessEmail: formData.businessEmail,
        employeeCount: parseInt(formData.employeeCount),
        logo: logoPreview, // Aquí deberías implementar la subida del logo a Firebase Storage
        createdAt: new Date().toISOString(),
        status: 'pending'
      });

      // Crear documento de usuario
      await setDoc(doc(db, 'users', userCredential.user.uid), {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        role: 'business',
        businessId: businessRef.id,
        createdAt: new Date().toISOString(),
        status: 'pending',
        language: i18n.language
      });

      // Después del registro exitoso
      toast.success(t('auth.registrationSuccess'));
      navigate('/business/dashboard');
    } catch (error: any) {
      console.error('Error en el registro:', error);
      setError(error.message || t('auth.registrationError'));
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    return (
      <div className="animate-fadeIn">
        {currentStep === 1 && (
          <div className="space-y-6">
            <div className="relative group">
              <label className="flex items-center gap-2 text-gray-700 font-semibold mb-2 transition-colors duration-200 group-focus-within:text-red-600">
                <FaUser className="text-red-500" /> {t('auth.firstName')}
              </label>
              <div className="relative">
                <input 
                  type="text" 
                  name="firstName" 
                  placeholder={t('auth.yourName')} 
                  value={formData.firstName} 
                  onChange={handleChange} 
                  required 
                  className="w-full p-4 pl-12 border-2 border-gray-200 rounded-xl text-base transition-all duration-300 bg-white/90 focus:outline-none focus:border-red-500 focus:shadow-lg focus:shadow-red-500/20 focus:bg-white group-hover:border-gray-300" 
                />
                <FaUser className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors duration-200" />
              </div>
            </div>
            <div className="relative group">
              <label className="flex items-center gap-2 text-gray-700 font-semibold mb-2 transition-colors duration-200 group-focus-within:text-red-600">
                <FaUserTie className="text-red-500" /> {t('auth.lastName')}
              </label>
              <div className="relative">
                <input 
                  type="text" 
                  name="lastName" 
                  placeholder={t('auth.yourLastName')} 
                  value={formData.lastName} 
                  onChange={handleChange} 
                  required 
                  className="w-full p-4 pl-12 border-2 border-gray-200 rounded-xl text-base transition-all duration-300 bg-white/90 focus:outline-none focus:border-red-500 focus:shadow-lg focus:shadow-red-500/20 focus:bg-white group-hover:border-gray-300" 
                />
                <FaUserTie className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors duration-200" />
              </div>
            </div>
            <div className="relative group">
              <label className="flex items-center gap-2 text-gray-700 font-semibold mb-2 transition-colors duration-200 group-focus-within:text-red-600">
                <FaEnvelope className="text-red-500" /> {t('auth.email')}
              </label>
              <div className="relative">
                <input 
                  type="email" 
                  name="email" 
                  placeholder={t('auth.yourEmail')} 
                  value={formData.email} 
                  onChange={handleChange} 
                  required 
                  className="w-full p-4 pl-12 border-2 border-gray-200 rounded-xl text-base transition-all duration-300 bg-white/90 focus:outline-none focus:border-red-500 focus:shadow-lg focus:shadow-red-500/20 focus:bg-white group-hover:border-gray-300" 
                />
                <FaEnvelope className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors duration-200" />
              </div>
            </div>
            <div className="relative group">
              <label className="flex items-center gap-2 text-gray-700 font-semibold mb-2 transition-colors duration-200 group-focus-within:text-red-600">
                <FaLock className="text-red-500" /> {t('auth.password')}
              </label>
              <div className="relative">
                <input 
                  type={showPassword ? 'text' : 'password'} 
                  name="password" 
                  placeholder={t('auth.passwordPlaceholder')} 
                  value={formData.password} 
                  onChange={handleChange} 
                  required 
                  className="w-full p-4 pl-12 pr-12 border-2 border-gray-200 rounded-xl text-base transition-all duration-300 bg-white/90 focus:outline-none focus:border-red-500 focus:shadow-lg focus:shadow-red-500/20 focus:bg-white group-hover:border-gray-300" 
                />
                <FaLock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors duration-200" />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>
            <div className="relative group">
              <label className="flex items-center gap-2 text-gray-700 font-semibold mb-2 transition-colors duration-200 group-focus-within:text-red-600">
                <FaLock className="text-red-500" /> {t('auth.confirmPassword')}
              </label>
              <div className="relative">
                <input 
                  type={showConfirmPassword ? 'text' : 'password'} 
                  name="confirmPassword" 
                  placeholder={t('auth.passwordPlaceholder')} 
                  value={formData.confirmPassword} 
                  onChange={handleChange} 
                  required 
                  className="w-full p-4 pl-12 pr-12 border-2 border-gray-200 rounded-xl text-base transition-all duration-300 bg-white/90 focus:outline-none focus:border-red-500 focus:shadow-lg focus:shadow-red-500/20 focus:bg-white group-hover:border-gray-300" 
                />
                <FaLock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors duration-200" />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                >
                  {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>
          </div>
        )}
        {currentStep === 2 && (
          <div className="space-y-6">
            <div className="relative group">
              <label className="flex items-center gap-2 text-gray-700 font-semibold mb-2 transition-colors duration-200 group-focus-within:text-red-600">
                <FaBuilding className="text-red-500" /> {t('auth.country')}
              </label>
              <div className="relative">
                <select 
                  name="country" 
                  value={formData.country} 
                  onChange={handleChange} 
                  required 
                  className="w-full p-4 pl-12 border-2 border-gray-200 rounded-xl text-base transition-all duration-300 bg-white/90 focus:outline-none focus:border-red-500 focus:shadow-lg focus:shadow-red-500/20 focus:bg-white group-hover:border-gray-300 cursor-pointer appearance-none"
                >
                  <option value="">{t('auth.selectCountry')}</option>
                  <option value="EC">{t('auth.ecuador')}</option>
                  <option value="US">{t('auth.unitedStates')}</option>
                </select>
                <FaBuilding className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors duration-200 pointer-events-none" />
              </div>
            </div>
            <div className="relative group">
              <label className="flex items-center gap-2 text-gray-700 font-semibold mb-2 transition-colors duration-200 group-focus-within:text-red-600">
                <FaBuilding className="text-red-500" /> {t('auth.businessIdType')}
              </label>
              <div className="relative">
                <select 
                  name="businessIdType" 
                  value={formData.businessIdType} 
                  onChange={handleChange} 
                  required 
                  className="w-full p-4 pl-12 border-2 border-gray-200 rounded-xl text-base transition-all duration-300 bg-white/90 focus:outline-none focus:border-red-500 focus:shadow-lg focus:shadow-red-500/20 focus:bg-white group-hover:border-gray-300 cursor-pointer appearance-none"
                >
                  <option value="">{t('auth.selectBusinessIdType')}</option>
                  {formData.country === 'EC' ? (
                    <>
                      <option value="RUC">{t('auth.ruc')}</option>
                      <option value="RIMPE">{t('auth.rimpe')}</option>
                    </>
                  ) : (
                    <>
                      <option value="EIN">{t('auth.ein')}</option>
                      <option value="DUNS">{t('auth.duns')}</option>
                    </>
                  )}
                </select>
                <FaBuilding className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors duration-200 pointer-events-none" />
              </div>
            </div>
            <div className="relative group">
              <label className="flex items-center gap-2 text-gray-700 font-semibold mb-2 transition-colors duration-200 group-focus-within:text-red-600">
                <FaBuilding className="text-red-500" /> {t('auth.businessId')}
              </label>
              <div className="relative">
                <input 
                  type="text" 
                  name="businessId" 
                  placeholder={t('auth.businessId')} 
                  value={formData.businessId} 
                  onChange={handleChange} 
                  required 
                  className="w-full p-4 pl-12 border-2 border-gray-200 rounded-xl text-base transition-all duration-300 bg-white/90 focus:outline-none focus:border-red-500 focus:shadow-lg focus:shadow-red-500/20 focus:bg-white group-hover:border-gray-300" 
                />
                <FaBuilding className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors duration-200" />
              </div>
            </div>
            <div className="relative group">
              <label className="flex items-center gap-2 text-gray-700 font-semibold mb-2 transition-colors duration-200 group-focus-within:text-red-600">
                <FaBuilding className="text-red-500" /> {t('auth.companyName')}
              </label>
              <div className="relative">
                <input 
                  type="text" 
                  name="companyName" 
                  placeholder={t('auth.companyName')} 
                  value={formData.companyName} 
                  onChange={handleChange} 
                  required 
                  className="w-full p-4 pl-12 border-2 border-gray-200 rounded-xl text-base transition-all duration-300 bg-white/90 focus:outline-none focus:border-red-500 focus:shadow-lg focus:shadow-red-500/20 focus:bg-white group-hover:border-gray-300" 
                />
                <FaBuilding className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors duration-200" />
              </div>
            </div>
            <div className="relative group">
              <label className="flex items-center gap-2 text-gray-700 font-semibold mb-2 transition-colors duration-200 group-focus-within:text-red-600">
                <FaBuilding className="text-red-500" /> {t('auth.companyType')}
              </label>
              <div className="relative">
                <select 
                  name="companyType" 
                  value={formData.companyType} 
                  onChange={handleChange} 
                  required 
                  className="w-full p-4 pl-12 border-2 border-gray-200 rounded-xl text-base transition-all duration-300 bg-white/90 focus:outline-none focus:border-red-500 focus:shadow-lg focus:shadow-red-500/20 focus:bg-white group-hover:border-gray-300 cursor-pointer appearance-none"
                >
                  <option value="">{t('auth.selectCompanyType')}</option>
                  {formData.country === 'EC' ? (
                    <>
                      <option value="SA">{t('auth.sa')}</option>
                      <option value="LTDA">{t('auth.ltda')}</option>
                      <option value="CONSORCIO">{t('auth.consorcio')}</option>
                      <option value="SAS">{t('auth.sas')}</option>
                    </>
                  ) : (
                    <>
                      <option value="LLC">{t('auth.llc')}</option>
                      <option value="INC">{t('auth.inc')}</option>
                      <option value="CORP">{t('auth.corp')}</option>
                    </>
                  )}
                </select>
                <FaBuilding className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors duration-200 pointer-events-none" />
              </div>
            </div>
          </div>
        )}
        {currentStep === 3 && (
          <div className="space-y-6">
            <div className="relative group">
              <label className="flex items-center gap-2 text-gray-700 font-semibold mb-2 transition-colors duration-200 group-focus-within:text-red-600">
                <FaMapMarkerAlt className="text-red-500" /> {t('auth.address')}
              </label>
              <div className="relative">
                <input 
                  type="text" 
                  name="address" 
                  placeholder={t('auth.address')} 
                  value={formData.address} 
                  onChange={handleChange} 
                  required 
                  className="w-full p-4 pl-12 border-2 border-gray-200 rounded-xl text-base transition-all duration-300 bg-white/90 focus:outline-none focus:border-red-500 focus:shadow-lg focus:shadow-red-500/20 focus:bg-white group-hover:border-gray-300" 
                />
                <FaMapMarkerAlt className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors duration-200" />
              </div>
            </div>
            <div className="relative group">
              <label className="flex items-center gap-2 text-gray-700 font-semibold mb-2 transition-colors duration-200 group-focus-within:text-red-600">
                <FaPhone className="text-red-500" /> {t('auth.phone')}
              </label>
              <div className="relative">
                <input 
                  type="tel" 
                  name="phone" 
                  placeholder={t('auth.phone')} 
                  value={formData.phone} 
                  onChange={handleChange} 
                  required 
                  className="w-full p-4 pl-12 border-2 border-gray-200 rounded-xl text-base transition-all duration-300 bg-white/90 focus:outline-none focus:border-red-500 focus:shadow-lg focus:shadow-red-500/20 focus:bg-white group-hover:border-gray-300" 
                />
                <FaPhone className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors duration-200" />
              </div>
            </div>
            <div className="relative group">
              <label className="flex items-center gap-2 text-gray-700 font-semibold mb-2 transition-colors duration-200 group-focus-within:text-red-600">
                <FaEnvelope className="text-red-500" /> {t('auth.businessEmail')}
              </label>
              <div className="relative">
                <input 
                  type="email" 
                  name="businessEmail" 
                  placeholder={t('auth.businessEmail')} 
                  value={formData.businessEmail} 
                  onChange={handleChange} 
                  required 
                  className="w-full p-4 pl-12 border-2 border-gray-200 rounded-xl text-base transition-all duration-300 bg-white/90 focus:outline-none focus:border-red-500 focus:shadow-lg focus:shadow-red-500/20 focus:bg-white group-hover:border-gray-300" 
                />
                <FaEnvelope className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors duration-200" />
              </div>
            </div>
            <div className="relative group">
              <label className="flex items-center gap-2 text-gray-700 font-semibold mb-2 transition-colors duration-200 group-focus-within:text-red-600">
                <FaBuilding className="text-red-500" /> {t('auth.employeeCount')}
              </label>
              <div className="relative">
                <input 
                  type="number" 
                  name="employeeCount" 
                  placeholder={t('auth.employeeCount')} 
                  value={formData.employeeCount} 
                  onChange={handleChange} 
                  required 
                  className="w-full p-4 pl-12 border-2 border-gray-200 rounded-xl text-base transition-all duration-300 bg-white/90 focus:outline-none focus:border-red-500 focus:shadow-lg focus:shadow-red-500/20 focus:bg-white group-hover:border-gray-300" 
                />
                <FaBuilding className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors duration-200" />
              </div>
            </div>
          </div>
        )}
        {currentStep === 4 && (
          <div className="space-y-6">
            <div className="relative group">
              <label className="flex items-center gap-2 text-gray-700 font-semibold mb-2 transition-colors duration-200">
                <FaUpload className="text-red-500" /> {t('auth.logo')}
              </label>
              <div 
                className="flex flex-col items-center gap-4 p-8 border-2 border-dashed border-gray-200 rounded-xl cursor-pointer transition-all duration-300 hover:border-red-500 hover:bg-red-50/50 group" 
                onClick={() => document.getElementById('logoInput')?.click()}
              >
                {logoPreview ? (
                  <div className="relative group">
                    <img src={logoPreview} alt="Logo preview" className="max-w-xs max-h-52 object-contain rounded-lg shadow-lg" />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg flex items-center justify-center">
                      <FaUpload className="text-white text-2xl" />
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center group-hover:bg-red-200 transition-colors duration-300">
                      <FaUpload className="text-red-500 text-2xl" />
                    </div>
                    <p className="text-gray-600 font-medium">{t('auth.uploadLogo')}</p>
                    <p className="text-gray-400 text-sm">{t('auth.clickToSelectLogo')}</p>
                  </>
                )}
                <input id="logoInput" type="file" accept="image/*" onChange={handleLogoChange} style={{ display: 'none' }} />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="w-full h-full flex justify-center items-center animate-fadeIn">
      <div className="bg-white/95 p-8 rounded-2xl shadow-2xl w-full max-w-lg relative z-10 backdrop-blur-md border border-white/20 transform transition-all duration-500 hover:scale-[1.02] hover:shadow-3xl">
        {/* Logo con animación */}
        <div className="flex justify-center mb-8">
          <div className="relative group">
            <img 
              src="https://firebasestorage.googleapis.com/v0/b/brix-v2.firebasestorage.app/o/Marca%2FRecurso%205.svg?alt=media&token=65d56a39-541f-4ec0-864f-fc2105cf9c54" 
              alt="Brix Logo" 
              className="w-32 max-h-20 object-contain drop-shadow-lg transition-all duration-500 group-hover:scale-110 group-hover:drop-shadow-2xl"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
          </div>
        </div>

        {/* Título con efecto de gradiente */}
        <h1 className="text-3xl font-bold text-center mb-8 bg-gradient-to-r from-gray-800 via-red-600 to-gray-800 bg-clip-text text-transparent animate-pulse">
          {t('auth.businessRegister')}
        </h1>

        {/* Indicador de progreso mejorado */}
        <div className="flex justify-center gap-3 mb-8">
          {[1, 2, 3, 4].map(step => (
            <div key={step} className="relative">
              <div className={`w-4 h-4 rounded-full transition-all duration-500 flex items-center justify-center ${
                currentStep === step 
                  ? 'bg-red-500 scale-110 shadow-lg shadow-red-500/50' 
                  : currentStep > step 
                    ? 'bg-green-500 scale-100' 
                    : 'bg-gray-200 scale-100'
              }`}>
                {currentStep > step && <FaCheck className="text-white text-xs" />}
              </div>
              {step < 4 && (
                <div className={`absolute top-2 left-4 w-8 h-0.5 transition-all duration-500 ${
                  currentStep > step ? 'bg-green-500' : 'bg-gray-200'
                }`}></div>
              )}
            </div>
          ))}
        </div>

        {/* Mensaje de error con animación */}
        {error && (
          <div className="mb-6 p-4 bg-gradient-to-r from-red-50 to-red-100 border-l-4 border-red-500 rounded-lg animate-shake">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700 font-medium">{error}</p>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {renderStep()}
          
          {/* Botones de navegación */}
          <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
            {currentStep > 1 && (
              <button 
                type="button" 
                onClick={prevStep} 
                disabled={loading} 
                className="px-6 py-3 rounded-xl font-semibold bg-gray-200 text-gray-900 transition-all duration-300 hover:bg-gray-300 hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none flex items-center gap-2 group"
              >
                <FaArrowLeft className="group-hover:-translate-x-1 transition-transform duration-200" />
                {t('common.back')}
              </button>
            )}
            {currentStep < 4 ? (
              <button 
                type="button" 
                onClick={nextStep} 
                disabled={loading} 
                className="px-6 py-3 rounded-xl font-semibold bg-gradient-to-r from-red-500 to-red-600 text-white transition-all duration-300 hover:from-red-600 hover:to-red-700 hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none flex items-center gap-2 group ml-auto"
              >
                {t('common.next')}
                <FaArrowRight className="group-hover:translate-x-1 transition-transform duration-200" />
              </button>
            ) : (
              <button 
                type="submit" 
                disabled={loading} 
                className="px-6 py-3 rounded-xl font-semibold bg-gradient-to-r from-red-500 to-red-600 text-white transition-all duration-300 hover:from-red-600 hover:to-red-700 hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none flex items-center gap-2 group ml-auto"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    {t('auth.registering')}
                  </>
                ) : (
                  <>
                    <FaCheck />
                    {t('auth.register')}
                  </>
                )}
              </button>
            )}
          </div>
        </form>

        {/* Enlace de navegación */}
        <div className="flex justify-center mt-8 pt-6 border-t border-gray-200">
          <p className="text-gray-600 text-sm">
            {t('auth.alreadyHaveAccount')}
            <a href="/auth/login" className="text-red-500 font-semibold ml-2 hover:underline transition-all duration-200 hover:text-red-600">
              {t('auth.login')}
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}; 